/**
 *
 * Created Date: 2019-03-08, 22:28:45 (zhenliang.sun)
 * Last Modified: 2019-07-29, 17:07:20 (zhenliang.sun)
 * Email: zhenliang.sun@gmail.com
 *
 * Distributed under the MIT license. See LICENSE file for details.
 * Copyright (c) 2019 vhall
 */

/**
 * IndexedDB 简单封装
 * This IndexedDB fork from https://github.com/tangshuang/hello-indexeddb
 * commit hash: 7f4c3fabd0d86db6c9a5d60c49c0c28caa69fc48
 * Version: 2.0.2
 * Author is named tangshuang
 * guidelines: https://www.tangshuang.net/5668.html
 * grammar and API: https://www.tangshuang.net/5681.html#title-1
 * thanks for this great library for operating IndexedDB just like relation database.
 *
 * @export
 * @class DocModuleDB
 * @author zhenliang.sun
 */
import { DBStore } from './DBStore';
import { modifyError } from './DBUtil';

// const IDB = window.indexedDB || window.webkitindexedDB || window.msIndexedDB || window.mozIndexedDB
// const IDBT = window.IDBTransaction = window.IDBTransaction || window.webkitIDBTransaction || window.msIDBTransaction
// const IDBTR = window.IDBKeyRange = window.IDBKeyRange || window.webkitIDBKeyRange || window.msIDBKeyRange

export default class DocModuleDB {
  constructor(options = {}) {
    let { name, version = 1, stores } = options;

    if (!name) {
      throw new Error('[DocModuleDB]: you should pass `name` option.');
    }

    if (!stores || !Array.isArray(stores) || !stores.length) {
      throw new Error('[DocModuleDB]: you should pass `stores` option.');
    }

    this.name = name;
    this.version = version;
    this.stores = stores;

    // update database structure
    const request = indexedDB.open(name, version);
    request.onupgradeneeded = e => {
      let db = e.target.result;
      let existStoreNames = Array.from(db.objectStoreNames);
      let passStoreNames = [];

      stores.forEach(item => {
        let objectStore = null;
        if (existStoreNames.indexOf(item.name) > -1) {
          objectStore = e.target.transaction.objectStore(item.name);
        } else {
          let keyPath = item.isKeyValue ? 'key' : item.keyPath;
          let autoIncrement = item.isKeyValue ? false : item.autoIncrement;
          objectStore = db.createObjectStore(item.name, { keyPath, autoIncrement });
        }

        // delete old indexes
        let indexNames = objectStore.indexNames;
        if (indexNames && indexNames.length) {
          Array.from(indexNames).forEach(item => objectStore.deleteIndex(item));
        }

        // add new indexes
        if (item.indexes && item.indexes.length) {
          item.indexes.forEach(item => {
            objectStore.createIndex(item.name, item.keyPath || item.name, {
              unique: item.unique,
              multiEntry: Array.isArray(item.keyPath)
            });
          });
        }

        passStoreNames.push(item.name);
      });

      // delete objectStores which is not in config information
      if (existStoreNames) {
        existStoreNames.forEach(item => {
          if (passStoreNames.indexOf(item) === -1) {
            db.deleteObjectStore(item);
          }
        });
      }
    };

    this.using = {};
  }
  connect() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.name, this.version);
      request.onerror = e => {
        reject(modifyError(e));
      };
      request.onsuccess = e => {
        resolve(e.target.result);
      };
    });
  }
  use(name) {
    const currentStore = this.stores.find(item => item.name === name);

    if (!currentStore) {
      throw new Error(`[DocModuleDB]: store ${name} is not existing.`);
    }

    // use connected store
    if (this.using[name]) {
      return this.using[name];
    }

    const store = new DBStore({
      db: this,
      store: currentStore
    });

    // if it is a key-value store, append special methods
    if (currentStore.isKeyValue) {
      store.key = i => store.keys().then(keys => keys && keys[i]);
      store.getItem = key => store.get(key).then(obj => obj && obj.value);
      store.setItem = (key, value) => store.put({ key, value });
      store.removeItem = key => store.delete(key);
    }

    this.using[name] = store;

    return store;
  }
  close() {
    this.using = null;
    this.stores = null;

    return this.connect().then(db => {
      db.close();
    });
  }
}
